import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AccessDenied } from '~/components/common/access-denied/access-denied';
import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { useCostAccess, type CostAccess } from '~/hooks/useCanSeeCost';

/**
 * A cost page behind an access answer: the page once the viewer may see cost, a skeleton while a
 * customer's answer loads (never a flash of amounts), else Access denied. The service whose gate
 * the page shows supplies `access` (workflow-service here, catalog-service on catalog-v2).
 */
export function CostAccessView({ access, children }: { access: CostAccess; children: ReactNode }) {
  const { t } = useTranslation('common');
  if (access.canSeeCost) return <>{children}</>;
  if (!access.settled) return <PageSkeleton />;
  return <AccessDenied reason={t('costEstimate.customer.accessDenied')} />;
}

/**
 * Gate for workflow-service's cost pages (Cost approvals, estimate details — the Slack card's
 * "Open estimate details" target). Gated in the component, not `beforeLoad`, because who may see
 * cost includes the server's answer (cost approvers, other internal domains).
 */
export function CostAccessGate({ children }: { children: ReactNode }) {
  return <CostAccessView access={useCostAccess()}>{children}</CostAccessView>;
}
